import React from 'react';
import { motion } from 'framer-motion';
import { Maximize, Droplets, TrendingDown } from 'lucide-react';
const causes = [{
  icon: Maximize,
  title: 'Vergrößerung der Prostata',
  text: 'Die benigne Prostatahyperplasie (BPH) übt direkten Druck auf die Harnröhre aus. Dies verhindert eine vollständige Entleerung der Blase und führt zu ständigem Harndrang, selbst wenn die Blase nicht voll ist.'
}, {
  icon: Droplets,
  title: 'Harnwegsirritationen',
  text: 'Chronische Reizungen des Harntrakts senden fehlerhafte Signale an das Gehirn. Die Blasenmuskulatur zieht sich zusammen, obwohl noch Kapazität vorhanden wäre — das Ergebnis ist plötzlicher, zwingender Harndrang.'
}, {
  icon: TrendingDown,
  title: 'Hormonelle Ungleichgewichte',
  text: 'Mit zunehmendem Alter sinkt der Testosteronspiegel, während Dihydrotestosteron (DHT) und Östrogen relativ ansteigen. Dieses Ungleichgewicht fördert das Wachstum des Prostatagewebes und stört den Schlafzyklus.'
}];
export function CausesSection() {
  return <section className="py-28 md:py-32 bg-white text-slate-900 relative overflow-hidden" data-id="element-39">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-40">
        {/* Header */}
        <div className="text-center mb-16 max-w-3xl mx-auto" data-id="element-41">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1
        }} className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold text-slate-900 mb-6 leading-tight" data-id="element-42">
            Verstehen Sie, was Ihren nächtlichen Schlaf stört.
          </motion.h2>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.2
        }} className="text-xl text-slate-600 leading-relaxed" data-id="element-43">
            Häufiges nächtliches Erwachen ist oft ein Symptom tieferliegender
            physiologischer Veränderungen. Das Verständnis dieser Ursachen ist
            der erste Schritt zur Wiederherstellung Ihrer Nachtruhe.
          </motion.p>
        </div>

        {/* Visual Diagram: Prostate Comparison */}
        <div className="mb-20 grid grid-cols-1 md:grid-cols-2 gap-12 items-center justify-center max-w-4xl mx-auto" data-id="element-44">
          {/* Normal Prostate */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.3
        }} className="flex flex-col items-center" data-id="element-45">
            <div className="relative w-48 h-48 bg-slate-50 rounded-full border border-slate-200 flex items-center justify-center mb-6" data-id="element-46">
              {/* Bladder neck */}
              <div className="absolute top-0 w-16 h-8 bg-brand-100 rounded-b-xl" data-id="element-47" />
              {/* Prostate tissue */}
              <div className="w-32 h-24 bg-brand-200 rounded-3xl flex items-center justify-center relative" data-id="element-48">
                {/* Urethra (wide open) */}
                <div className="w-4 h-full bg-white/80 rounded-full" data-id="element-49" />
              </div>
              {/* Urethra exit */}
              <div className="absolute bottom-0 w-4 h-8 bg-brand-100/50" data-id="element-50" />
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-2" data-id="element-51">
              Normale Prostata
            </h3>
            <span className="inline-flex items-center px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 text-sm font-medium border border-emerald-100" data-id="element-52">
              Freier Harnfluss
            </span>
          </motion.div>

          {/* Enlarged Prostate */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.4
        }} className="flex flex-col items-center" data-id="element-53">
            <div className="relative w-48 h-48 bg-slate-50 rounded-full border border-slate-200 flex items-center justify-center mb-6" data-id="element-54">
              {/* Bladder neck */}
              <div className="absolute top-0 w-16 h-8 bg-brand-100 rounded-b-xl" data-id="element-55" />
              {/* Prostate tissue (Enlarged & Pulsing) */}
              <motion.div animate={{
              scale: [1, 1.05, 1]
            }} transition={{
              duration: 4,
              repeat: Infinity,
              ease: 'easeInOut'
            }} className="w-40 h-32 bg-brand-300 rounded-3xl flex items-center justify-center relative shadow-lg shadow-brand-500/10" data-id="element-56">
                {/* Urethra (compressed) */}
                <div className="w-1 h-full bg-white/60 rounded-full" data-id="element-57" />
              </motion.div>
              {/* Urethra exit */}
              <div className="absolute bottom-0 w-4 h-8 bg-brand-100/50" data-id="element-58" />
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-2" data-id="element-59">
              Vergrößerte Prostata
            </h3>
            <span className="inline-flex items-center px-3 py-1 rounded-full bg-red-50 text-red-700 text-sm font-medium border border-red-100" data-id="element-60">
              Eingeschränkter Harnfluss
            </span>
          </motion.div>
        </div>

        {/* Cause Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-61">
          {causes.map((cause, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.5 + index * 0.1
        }} className="bg-slate-50 p-8 rounded-xl border border-slate-200 hover:border-brand-200 hover:shadow-md transition-all duration-300 group" data-id="element-62">
              <div className="w-12 h-12 rounded-lg bg-white border border-slate-200 flex items-center justify-center mb-6 group-hover:border-brand-200 transition-colors" data-id="element-63">
                <cause.icon className="w-6 h-6 text-brand-500" strokeWidth={1.5} data-id="element-64" />
              </div>
              <h3 className="text-xl font-serif font-bold text-slate-900 mb-4 group-hover:text-brand-600 transition-colors" data-id="element-65">
                {cause.title}
              </h3>
              <p className="text-slate-600 leading-relaxed text-base" data-id="element-66">
                {cause.text}
              </p>
            </motion.div>)}
        </div>
      </div>
    </section>;
}